import { z } from 'zod';

// Base API Response Schema
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  // Backend sends LocalDateTime without a timezone (e.g. "2026-07-10T10:03:15.109041295"),
  // so accept any string rather than a strict ISO-8601-with-offset datetime.
  timestamp: z.string(),
  requestId: z.string().uuid(),
});

// Paginated Response Schema
export const PaginatedResponseSchema = z.object({
  data: z.array(z.unknown()),
  pagination: z.object({
    page: z.number().min(1),
    limit: z.number().min(1).max(100),
    total: z.number().min(0),
    totalPages: z.number().min(0),
    hasNext: z.boolean(),
    hasPrev: z.boolean(),
  }),
});

// Success Response Schema
export const SuccessResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  ApiResponseSchema.extend({
    success: z.literal(true),
    data: dataSchema,
  });

// Error Response Schema
export const ErrorResponseSchema = ApiResponseSchema.extend({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    type: z.enum(['VALIDATION', 'AUTHENTICATION', 'AUTHORIZATION', 'NOT_FOUND', 'SERVER_ERROR', 'NETWORK_ERROR']),
    details: z.record(z.string(), z.any()).optional(),
    stack: z.string().optional(),
  }),
});

// HTTP Method Types
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// Request Configuration
export interface RequestConfig {
  method: HttpMethod;
  url: string;
  data?: unknown;
  params?: Record<string, string | number | boolean>;
  headers?: Record<string, string>;
  timeout?: number;
  retries?: number;
  cache?: boolean;
  cacheTTL?: number;
  withCredentials?: boolean;
}

// API Response Types
export type ApiResponse<T = unknown> = z.infer<ReturnType<typeof SuccessResponseSchema<z.ZodType<T>>>>;
export type ApiErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type PaginatedResponse<T = unknown> = ApiResponse<z.infer<typeof PaginatedResponseSchema> & { data: T[] }>;

// Request/Response Interceptor Types
export interface RequestInterceptor {
  onFulfilled?: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
  onRejected?: (error: unknown) => unknown;
}

export interface ResponseInterceptor {
  onFulfilled?: <T>(response: ApiResponse<T>) => ApiResponse<T> | Promise<ApiResponse<T>>;
  onRejected?: (error: ApiErrorResponse) => ApiErrorResponse | Promise<ApiErrorResponse>;
}

// Cache Configuration
export interface CacheConfig {
  enabled: boolean;
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum cache size
  strategy: 'memory' | 'sessionStorage' | 'localStorage';
}

// Retry Configuration
export interface RetryConfig {
  attempts: number;
  delay: number; // Base delay in milliseconds
  backoffFactor: number; // Exponential backoff multiplier
  retryCondition: (error: ApiErrorResponse) => boolean;
}

// Standardized pagination payload used by list endpoints
export interface PaginatedList<T> {
  items: T[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// API Client Configuration
export interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  headers: Record<string, string>;
  cache: CacheConfig;
  retry: RetryConfig;
  interceptors: {
    request: RequestInterceptor[];
    response: ResponseInterceptor[];
  };
}

// Generic API Hook State
export interface ApiState<T = unknown> {
  data: T | null;
  loading: boolean;
  error: ApiErrorResponse | null;
  success: boolean;
}

// API Hook Options
export interface ApiHookOptions {
  immediate?: boolean;
  cache?: boolean;
  retry?: boolean;
  onSuccess?: (data: unknown) => void;
  onError?: (error: ApiErrorResponse) => void;
  onFinally?: () => void;
}

// Environment Configuration
export interface EnvironmentConfig {
  API_BASE_URL: string;
  API_TIMEOUT: number;
  ENABLE_CACHE: boolean;
  ENABLE_RETRY: boolean;
  LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error';
}
